import { useTranslation } from '@gimlite/common';
import {
  Col,
  Description,
  Form,
  Space,
  Widget,
  WizardItemType,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { CreateFPSWizardContext } from '../../createFps.wizard';
import './bilan.step.scss';

export const BilanStep = ({
  context,
  validate,
  submit,
}: WizardItemType.Config.Component<CreateFPSWizardContext>) => {
  const { t, lang }: any = useTranslation();

  const formattedDate = useCallback(
    (date: string) =>
      t(`DATETIME_HUMAN`)
        .replace('[DATE]', DateTime.fromISO(date).toFormat(`DDDD`))
        .replace('[TIME]', DateTime.fromISO(date).toFormat(`TT`)),
    [lang],
  );

  const formattedAddress = useCallback((address: any) => {
    if (!address) return '';
    const addressClear = Object.fromEntries(
      Object.entries(address).map(([key, value]) => [
        key,
        `${new String(value || '').trim()}`,
      ]),
    );
    const {
      streetNumber,
      streetType,
      streetName,
      postalCode,
      addressLocality,
    } = addressClear;
    return `${streetNumber || ''} ${streetType || ''} ${streetName || ''}, ${postalCode || ''} ${addressLocality || ''}`.trim();
  }, []);

  const notDefined = useMemo(
    () => (
      <Write
        data={{ item: t('notDefined') }}
        config={{
          mode: 'value-small',
          color: 'disabled',
        }}
      />
    ),
    [lang],
  );

  return (
    <Form
      config={{ validateOnChange: true }}
      handleEvent={{
        change: (value: Object) => {
          submit(value);
        },
        validate: (value: boolean) => {
          validate(true);
        },
      }}
    >
      <Zone
        config={{
          zones: [['car', 'fps']],
          rows: ['1fr'],
          columns: ['1fr', '1fr'],
        }}
      >
        <Zone.Area config={{ area: 'car' }}>
          <Widget
            config={{
              title: t('vehicleInformation'),
              icon: 'faCarSolid',
              shadow: false,
            }}
          >
            <Col>
              <Space />
              <Description
                data={[
                  {
                    key: 'immatriculation',
                    label: t('immatriculation'),
                    value: context?.lpn || notDefined,
                  },
                  {
                    key: 'brand',
                    label: t('brand'),
                    value: context?.brand || notDefined,
                  },
                  {
                    key: 'country',
                    label: t('country'),
                    value: context?.country,
                  },
                  {
                    key: 'model',
                    label: t('model'),
                    value: context?.model || notDefined,
                  },
                ]}
                config={{ layout: 'column' }}
              />
            </Col>
          </Widget>
        </Zone.Area>
        <Zone.Area config={{ area: 'fps' }}>
          <Widget
            config={{
              icon: 'faFileSignatureSolid',
              title: t('fpsInformation'),
              shadow: false,
            }}
          >
            <Col>
              <Space />
              <Description
                data={[
                  {
                    key: 'dateTimeOfDispute',
                    label: t('dateTimeOfDispute'),
                    value: formattedDate(context?.exit?.date),
                  },
                  {
                    key: 'place',
                    label: t('place'),
                    value: formattedAddress(context?.address),
                  },
                  {
                    key: 'country',
                    label: t('country'),
                    value: 'France', // Donnée non disponible
                  },
                  // {
                  //   key: 'endValidity',
                  //   label: 'Fin de validité du FPS',
                  //   value: '26.11.2021 11h39',
                  // },
                  {
                    key: 'comment',
                    label: t('comment'),
                    value: <pre>{context?.comment}</pre>,
                  },
                ]}
                config={{ layout: 'column' }}
              />
            </Col>
          </Widget>
        </Zone.Area>
      </Zone>
    </Form>
  );
};
